import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { ClassificationAssignment } from "./ClassificationAssignment";
import {
  useGetTxnClassificationsQuery,
  useAddTxnClassificationMutation,
  useDeleteTxnClassificationMutation,
  useConfirmTxnClassificationMutation,
  useGetAllClassificationQuery,
} from "store/apis/classifications";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { useToast } from "hooks/useToast";
import { SuggestedClassification } from "types/Models/classification";

export const TxnClassificationAssignment = ({
  has_classes,
  has_suggestions = false,
  transactionId,
}: {
  has_classes: boolean;
  has_suggestions?: boolean;
  transactionId: string;
}) => {
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const { successToast } = useToast();

  const { data: assignedClasses = [], isLoading: isClassesLoading } =
    useGetTxnClassificationsQuery(
      {
        entityId,
        transactionId,
      },
      {
        skip: !entityId || !transactionId || (!has_classes && !has_suggestions),
      }
    );

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const getClassNameById = (classUuid: string) => {
    return allClassifications
      .flatMap((group) => group.classes)
      .find((cls) => cls.uuid === classUuid)?.name;
  };

  const [addTxnClassification, { isLoading: isAddingClass }] =
    useAddTxnClassificationMutation();
  const [deleteTxnClassification, { isLoading: isDeletingClass }] =
    useDeleteTxnClassificationMutation();
  const [confirmTxnClassification, { isLoading: isConfirmingClass }] =
    useConfirmTxnClassificationMutation();

  const assignedClassificationIds = assignedClasses.flatMap((group) =>
    group.classes.map((cls) => cls.uuid)
  );

  const suggestions: SuggestedClassification[] = assignedClasses.flatMap(
    (group) =>
      (group.suggestions ?? []).map((suggestion) => ({
        classUuid: suggestion.uuid,
        className: suggestion.name,
        groupUuid: group.uuid,
        groupName: group.name,
      }))
  );

  const handleAssignClass = async (
    classUuid: string,
    _groupUuid: string,
    existingClassToRemove?: string
  ) => {
    if (existingClassToRemove) {
      const removedClassName = getClassNameById(existingClassToRemove);

      await deleteTxnClassification({
        entityId,
        transactionId,
        classificationId: existingClassToRemove,
      }).unwrap();

      successToast({
        message: `"${removedClassName}" removed from the transaction`,
      });
    }
    const removedClassName = getClassNameById(classUuid);

    await addTxnClassification({
      entityId,
      transactionId,
      payload: { classification_id: classUuid },
    }).unwrap();

    successToast({
      message: `"${removedClassName}" added to the transaction`,
    });
  };

  const handleRemoveClass = async (classUuid: string) => {
    const removedClassName = getClassNameById(classUuid);
    await deleteTxnClassification({
      entityId,
      transactionId,
      classificationId: classUuid,
    }).unwrap();
    successToast({
      message: `"${removedClassName}" removed from the transaction`,
    });
  };

  const handleConfirmSuggestion = async (classUuid: string) => {
    const className = getClassNameById(classUuid);

    await confirmTxnClassification({
      entityId,
      transactionId,
      classificationId: classUuid,
    }).unwrap();

    successToast({ message: `"${className}" added to the transaction` });
  };

  return (
    <ClassificationAssignment
      entityId={entityId}
      assignedClassificationIds={assignedClassificationIds}
      onAssignClass={handleAssignClass}
      onRemoveClass={handleRemoveClass}
      suggestions={suggestions}
      onConfirmSuggestion={handleConfirmSuggestion}
      isUpdating={
        isAddingClass ||
        isDeletingClass ||
        isConfirmingClass ||
        isClassesLoading
      }
      readOnly={!hasWritePermission}
      label=""
      variant="table"
      blockedUI={
        <Combobox
          placeholder="Select"
          menuPortalTarget={document.body}
          isDisabled
          value={[]}
          options={[]}
        />
      }
    />
  );
};
